import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import {
  Assignment,
  Close,
  InfoOutlined,
  NotInterested,
  TimerOff,
  TrendingFlat,
  VisibilityOff
} from '@mui/icons-material'
import ArchiveIcon from '@mui/icons-material/Archive'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { DatePicker } from '@mui/lab'
import {
  DropDownSelect,
  InlineError,
  Labels,
  MainContent,
  RejectionProbabilityInfo,
  ServerSideDataGrid,
  StyledLink,
  StyledTab,
  StyledTabs,
  TableRejectionRates,
  ToggleFlag,
  Validated
} from 'components'
import LabelToggle, { unlabeled } from 'components/LabelToggle'
import {
  ListLoadingStatus,
  paramsToSearchObject
} from 'components/ServerSideDataGrid'
import { addDays, format, formatDistanceToNowStrict, parseJSON } from 'date-fns'
import {
  COMPARE_RECRODS_GRAPH_COLORS,
  formatDuration,
  ValidationDate
} from 'helpers'
import useAllRows from 'hooks/useAllRows'
import { useEffect, useMemo, useRef, useState } from 'react'
import Plot from 'react-plotly.js'
import { useQuery } from 'react-query'
import { useHistory, useParams } from 'react-router-dom'
import { Flag, Role, api, authenticationService, useMetricsContext } from 'services'
import { CompareRecordsWorkspace } from '../CompareRecords'

const labelFilterValue = (labels) => labels.map((label) => label.id).join(',')
const recordsPerPageOptions = [
  { content: '10', value: '10' },
  { content: '20', value: '20' },
  { content: '50', value: '50' },
  { content: '100', value: '100' }
]
const emptyLabels = []

function sameLabelIds(currentLabels, nextLabels) {
  if (currentLabels.length !== nextLabels.length) {
    return false
  }

  return currentLabels.every(
    (label, index) => label.id === nextLabels[index]?.id
  )
}

function normalizeLabelOptions(labelResponse) {
  if (Array.isArray(labelResponse)) {
    return labelResponse
  }

  if (Array.isArray(labelResponse?.items)) {
    return labelResponse.items
  }

  return []
}

function paramsWithout(urlParams, excludedKeys) {
  const params = paramsToSearchObject(urlParams)
  excludedKeys.forEach((key) => delete params[key])
  Object.keys(params).forEach((key) => {
    if (params[key] === null || params[key] === undefined || params[key] === '') {
      delete params[key]
    }
  })
  return params
}

function selectedLabelsFromParam(labels, labelParam) {
  if (!labelParam) {
    return []
  }

  const labelIds = labelParam.split(',')
  const numericIds = labelIds.map((id) => parseInt(id))
  const selectedLabels = labels.filter((label) => numericIds.includes(label.id))

  if (labelIds.includes(unlabeled.id)) {
    selectedLabels.push(unlabeled)
  }

  return selectedLabels
}

function FilterSection({ children }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      minWidth={220}
      flexGrow={1}
    >
      {children}
    </Box>
  )
}

const RecordFlags = ({ record }) => {
  return (
    <Box display="flex" flexDirection="column">
      {record.exclude ? (
        <Chip
          size="small"
          color="default"
          icon={<Close />}
          label="Excluded"
          variant="outlined"
        />
      ) : null}

      <Role restrict={['staff']}>
        {record.hidden ? (
          <Chip
            size="small"
            color="default"
            icon={<VisibilityOff />}
            label="Hidden"
            variant="outlined"
          />
        ) : null}
        {record.test_run ? (
          <Chip
            size="small"
            color="primary"
            icon={<Assignment />}
            label="Test Run"
            variant="outlined"
          />
        ) : null}
        {record.metrics?.short_run_error ? (
          <Chip
            size="small"
            color="secondary"
            icon={<TimerOff />}
            label="Short Run"
            variant="outlined"
          />
        ) : null}
        {record.metrics?.flatline_error ? (
          <Chip
            size="small"
            color="secondary"
            icon={<TrendingFlat />}
            label="Flatline"
            variant="outlined"
          />
        ) : null}
        {record.metrics?.no_run_error ? (
          <Chip
            size="small"
            color="secondary"
            icon={<NotInterested />}
            label="No Run"
            variant="outlined"
          />
        ) : null}
      </Role>
    </Box>
  )
}

function OrganizationSelector({
  institutes = [],
  loading = false,
  value = [],
  onChange = () => null
}) {
  return (
    <Autocomplete
      multiple
      size="small"
      options={institutes}
      value={value}
      onChange={(event, nextValue) => onChange(nextValue)}
      getOptionLabel={(option) => option.name}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      filterSelectedOptions
      loading={loading}
      renderTags={(selectedInstitutes, getTagProps) =>
        selectedInstitutes.map((institute, index) => (
          <Chip
            {...getTagProps({ index })}
            key={institute.id}
            label={institute.name}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Organizations"
          placeholder="Choose organizations"
        />
      )}
    />
  )
}

function CrossOrgLinesTable({ instituteIds }) {
  const { metricType, metricLabel } = useMetricsContext()
  const [url, setUrl] = useState(new URL(window.location))
  const urlParams = useMemo(() => new URLSearchParams(url.search), [url.search])
  const [lineName, setLineName] = useState(urlParams.get('name__icontains') || '')
  const [showArchived, setShowArchived] = useState(
    urlParams.get('archived') === 'true'
  )
  const [selectedLabels, setSelectedLabels] = useState([])
  const [numLines, setNumLines] = useState('10')

  const { data: labelOptions = emptyLabels } = useQuery(
    ['cross-org-line-label-options', instituteIds, { resourceType: 'line' }],
    () =>
      api.crossOrg.labels
        .list({
          institute_ids: instituteIds,
          resource_type: 'line'
        })
        .then(normalizeLabelOptions),
    {
      enabled: Boolean(instituteIds)
    }
  )

  useEffect(() => {
    const nextSelectedLabels = selectedLabelsFromParam(
      labelOptions,
      url.searchParams.get('labels')
    )

    setSelectedLabels((currentLabels) =>
      sameLabelIds(currentLabels, nextSelectedLabels)
        ? currentLabels
        : nextSelectedLabels
    )
  }, [labelOptions, url.search])

  const updateUrlParam = (key, value) => {
    setUrl((currentUrl) => {
      const nextUrl = new URL(currentUrl)
      nextUrl.searchParams.delete('page')
      if (value === null || value === undefined || value === '') {
        nextUrl.searchParams.delete(key)
      } else {
        nextUrl.searchParams.set(key, value)
      }
      return nextUrl
    })
  }

  const handleLineNameChange = (event) => {
    const value = event.target.value
    setLineName(value)
    updateUrlParam('name__icontains', value)
  }

  const handleSelectedLabels = (event, labels) => {
    setSelectedLabels(labels)
    updateUrlParam('labels', labels.length > 0 ? labelFilterValue(labels) : null)
  }

  const handleShowArchivedChange = (event) => {
    const checked = event.target.checked
    setShowArchived(checked)
    updateUrlParam('archived', checked)
  }

  const handleClearFilters = () => {
    setLineName('')
    setShowArchived(false)
    setSelectedLabels([])
    setUrl((currentUrl) => {
      const nextUrl = new URL(currentUrl.origin + currentUrl.pathname)
      return nextUrl
    })
  }

  const searchParams = useMemo(
    () => ({
      ...paramsWithout(urlParams, ['institute_ids']),
      institute_ids: instituteIds,
      archived: showArchived
    }),
    [instituteIds, showArchived, urlParams]
  )

  const renderLineLength = (length) => {
    if (length === null || length === undefined || length === '') {
      return <Typography color="text.secondary">-</Typography>
    }

    return `${length} m`
  }

  const columns = useMemo(() => [
    { field: 'id', hide: true },
    { field: 'line_statistics', hide: true },
    { field: 'validation', hide: true },
    { field: 'labels', hide: true },
    { field: 'archived', hide: true },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params) => {
        const { id, name, labels, archived, validation } = params.row

        return (
          <Box display="flex" flexDirection="row" alignItems="flex-start">
            <Box alignSelf="center" height="24px" width="24px" pr={1}>
              {archived ? (
                <Tooltip title="Archived">
                  <ArchiveIcon color="disabled" />
                </Tooltip>
              ) : validation ? (
                <Flag name="validation">
                  <Tooltip
                    title={<ValidationDate line={params} variant="tooltip" />}
                  >
                    <span>
                      <Validated
                        justifyContent="flex-start"
                        validationId={validation.validation_id}
                      />
                    </span>
                  </Tooltip>
                </Flag>
              ) : null}
            </Box>
            <Box pr={2}>
              <StyledLink
                to={{
                  pathname: `/line/${id}`,
                  state: {
                    name: 'Cross Organization',
                    referrer: {
                      path: '/cross-org'
                    }
                  }
                }}
              >
                <Typography
                  color="inherit"
                  style={{ fontWeight: 600, whiteSpace: 'inherit' }}
                >
                  {name}
                </Typography>
              </StyledLink>
            </Box>
            <Labels
              shownLabels={labels && labels.slice(0, 2)}
              hiddenLabels={labels && labels.slice(2)}
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
            />
          </Box>
        )
      }
    },
    {
      field: 'institute',
      headerName: 'Organization',
      width: 180,
      renderCell: ({ value }) => value?.name || '-'
    },
    {
      field: 'records',
      headerName: 'Record Count',
      sortable: false,
      width: 180,
      renderCell: (params) => params.row?.line_statistics?.record_count ?? '-'
    },
    {
      field: 'vitalmetric',
      renderHeader: () => (
        <Typography style={{ fontSize: '18px', fontWeight: 600 }}>
          {metricLabel}
        </Typography>
      ),
      sortable: false,
      width: 150,
      renderCell: (params) => {
        const { line_statistics, validation } = params.row
        const lineStats = validation || line_statistics
        return lineStats ? lineStats[metricType]?.mean ?? '-' : '-'
      }
    },
    {
      field: 'length',
      headerName: 'Length (m)',
      sortable: false,
      width: 140,
      renderCell: ({ row }) => renderLineLength(row?.length)
    },
    {
      field: 'rejection',
      renderHeader: () => (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          fontWeight={600}
          fontSize={18}
        >
          {'Rejection Probability'}
          <Box display="flex" pl={1}>
            <RejectionProbabilityInfo />
          </Box>
        </Box>
      ),
      sortable: false,
      width: 250,
      renderCell: (params) => {
        const { line_statistics, validation } = params.row
        const lineStats = validation || line_statistics

        return lineStats?.[metricType]?.rejection_rates ? (
          <TableRejectionRates
            rejectionRates={lineStats[metricType].rejection_rates}
          />
        ) : (
          '-'
        )
      }
    }
  ], [metricLabel, metricType])

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start">
        <FilterSection>
          <Typography fontWeight={600}>Line Name</Typography>
          <TextField
            label="Line Name"
            size="small"
            value={lineName}
            onChange={handleLineNameChange}
          />
        </FilterSection>
        <FilterSection>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={600}>Labels</Typography>
            <Button size="small" color="secondary" onClick={() => handleSelectedLabels(null, [])}>
              Reset
            </Button>
          </Box>
          <LabelToggle
            labels={labelOptions}
            selectedLabels={selectedLabels}
            handleSelectedLabels={handleSelectedLabels}
          />
        </FilterSection>
        <Box pt={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showArchived}
                onChange={handleShowArchivedChange}
              />
            }
            label="Show Archived"
          />
        </Box>
        <Box pt={3}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </Box>
        <Box pt={3} ml="auto">
          <DropDownSelect
            labelId="cross-org-num-lines-select"
            label="Lines per Page"
            value={numLines}
            onValueChange={setNumLines}
            items={recordsPerPageOptions}
            styles={{ width: '130px' }}
          />
        </Box>
      </Box>
      <ServerSideDataGrid
        apiCall={api.crossOrg.lines.list}
        queryName="crossOrgLines"
        searchParams={searchParams}
        columns={columns}
        noRowsText="No lines found for the selected organizations"
        pageSize={Number(numLines)}
      />
    </Box>
  )
}

function CrossOrgRecordsTable({ instituteIds }) {
  const { metricType, metricLabel } = useMetricsContext()
  const previousInstituteIds = useRef(instituteIds)
  const [url, setUrl] = useState(new URL(window.location))
  const urlParams = useMemo(() => new URLSearchParams(url.search), [url.search])
  const [lineSelected, setLineSelected] = useState(
    urlParams.get('line') === null ? '' : Number(urlParams.get('line'))
  )
  const [validationSelected, setValidationSelected] = useState(
    urlParams.get('validation__id') === null
      ? ''
      : Number(urlParams.get('validation__id'))
  )
  const [selectedStartDate, setSelectedStartDate] = useState(() => {
    if (urlParams.has('upload_date__gte')) return urlParams.get('upload_date__gte')
    if (urlParams.has('date__gte')) return urlParams.get('date__gte')
    return null
  })
  const [selectedEndDate, setSelectedEndDate] = useState(() => {
    if (urlParams.has('upload_date__lt')) return urlParams.get('upload_date__lt')
    if (urlParams.has('date__lt')) return urlParams.get('date__lt')
    return null
  })
  const [dateTypeRadioButton, setDateTypeRadioButton] = useState(
    urlParams.has('upload_date__gte') || urlParams.has('upload_date__lt')
      ? 'uploaded'
      : 'recorded'
  )
  const [exclude, setExclude] = useState(
    urlParams.has('exclude') ? urlParams.get('exclude') : null
  )
  const [includeHidden, setIncludeHidden] = useState(
    urlParams.get('include_hidden') === 'true'
  )
  const [selectedLabels, setSelectedLabels] = useState([])
  const [numRecords, setNumRecords] = useState('10')

  const { data: lineResponse, isLoading: isLinesLoading } = useQuery(
    ['cross-org-line-options', instituteIds],
    () =>
      api.crossOrg.lines.list({
        size: 9999,
        institute_ids: instituteIds,
        archived: false
      }),
    {
      enabled: Boolean(instituteIds)
    }
  )

  const { data: validationOptions = [], isLoading: isValidationsLoading } =
    useQuery(
      ['cross-org-validation-options', instituteIds],
      async () => {
        const ids = instituteIds
          .split(',')
          .map((id) => id.trim())
          .filter((id) => id)

        const responses = await Promise.all(
          ids.map((id) =>
            api.validations.listForInstitute(id, {
              size: 9999
            })
          )
        )

        return responses
          .flatMap((response) => response.items || [])
          .sort((a, b) => {
            const orgCompare = (a.institute?.name || '').localeCompare(
              b.institute?.name || ''
            )
            return orgCompare || a.name.localeCompare(b.name)
          })
      },
      {
        enabled: Boolean(instituteIds)
      }
    )

  const { data: labelOptions = emptyLabels } = useQuery(
    ['cross-org-record-label-options', instituteIds, { resourceType: 'record' }],
    () =>
      api.crossOrg.labels
        .list({
          institute_ids: instituteIds,
          resource_type: 'record'
        })
        .then(normalizeLabelOptions),
    {
      enabled: Boolean(instituteIds)
    }
  )

  useEffect(() => {
    if (previousInstituteIds.current === instituteIds) {
      return
    }

    previousInstituteIds.current = instituteIds
    setLineSelected(null)
    setValidationSelected(null)
    setUrl((currentUrl) => {
      const nextUrl = new URL(currentUrl)
      nextUrl.searchParams.delete('line')
      nextUrl.searchParams.delete('validation__id')
      nextUrl.searchParams.delete('page')
      return nextUrl
    })
  }, [instituteIds])

  useEffect(() => {
    const nextSelectedLabels = selectedLabelsFromParam(
      labelOptions,
      url.searchParams.get('labels')
    )

    setSelectedLabels((currentLabels) =>
      sameLabelIds(currentLabels, nextSelectedLabels)
        ? currentLabels
        : nextSelectedLabels
    )
  }, [labelOptions, url.search])

  const updateUrlParam = (key, value) => {
    setUrl((currentUrl) => {
      const nextUrl = new URL(currentUrl)
      nextUrl.searchParams.delete('page')
      if (value === null || value === undefined || value === '') {
        nextUrl.searchParams.delete(key)
      } else {
        nextUrl.searchParams.set(key, value)
      }
      return nextUrl
    })
  }

  const handleLineChange = (event, line) => {
    const nextLine = line ? line.id : null
    setLineSelected(nextLine)
    updateUrlParam('line', nextLine)
  }

  const handleValidationChange = (event, validation) => {
    const nextValidation = validation ? validation.id : null
    setValidationSelected(nextValidation)
    updateUrlParam('validation__id', nextValidation)
  }

  const handleExclude = (event, value) => {
    setExclude(value)
    updateUrlParam('exclude', value)
  }

  const handleIncludeHidden = (event) => {
    const include = event.target.checked
    setIncludeHidden(include)
    updateUrlParam('include_hidden', include ? include : null)
  }

  const handleSelectedLabels = (event, labels) => {
    setSelectedLabels(labels)
    updateUrlParam('labels', labels.length > 0 ? labelFilterValue(labels) : null)
  }

  const handleRadioButtonChange = (event) => {
    const nextDateType = event.target.value
    setDateTypeRadioButton(nextDateType)
    setUrl((currentUrl) => {
      const nextUrl = new URL(currentUrl)
      nextUrl.searchParams.delete('page')
      if (nextDateType === 'uploaded') {
        if (nextUrl.searchParams.get('date__gte') !== null) {
          nextUrl.searchParams.set(
            'upload_date__gte',
            nextUrl.searchParams.get('date__gte')
          )
        }
        if (nextUrl.searchParams.get('date__lt') !== null) {
          nextUrl.searchParams.set(
            'upload_date__lt',
            nextUrl.searchParams.get('date__lt')
          )
        }
        nextUrl.searchParams.delete('date__gte')
        nextUrl.searchParams.delete('date__lt')
      } else {
        if (nextUrl.searchParams.get('upload_date__gte') !== null) {
          nextUrl.searchParams.set(
            'date__gte',
            nextUrl.searchParams.get('upload_date__gte')
          )
        }
        if (nextUrl.searchParams.get('upload_date__lt') !== null) {
          nextUrl.searchParams.set(
            'date__lt',
            nextUrl.searchParams.get('upload_date__lt')
          )
        }
        nextUrl.searchParams.delete('upload_date__gte')
        nextUrl.searchParams.delete('upload_date__lt')
      }
      return nextUrl
    })
  }

  const handleStartDateChange = (date) => {
    setSelectedStartDate(date)
    setUrl((currentUrl) => {
      const nextUrl = new URL(currentUrl)
      nextUrl.searchParams.delete('page')
      nextUrl.searchParams.delete('upload_date__gte')
      nextUrl.searchParams.delete('date__gte')
      if (date !== null) {
        nextUrl.searchParams.set(
          dateTypeRadioButton === 'uploaded' ? 'upload_date__gte' : 'date__gte',
          format(date, 'yyyy-MM-dd')
        )
      }
      return nextUrl
    })
  }

  const handleEndDateChange = (date) => {
    setSelectedEndDate(date)
    setUrl((currentUrl) => {
      const nextUrl = new URL(currentUrl)
      nextUrl.searchParams.delete('page')
      nextUrl.searchParams.delete('upload_date__lt')
      nextUrl.searchParams.delete('date__lt')
      if (date !== null) {
        nextUrl.searchParams.set(
          dateTypeRadioButton === 'uploaded' ? 'upload_date__lt' : 'date__lt',
          format(addDays(date, 1), 'yyyy-MM-dd')
        )
      }
      return nextUrl
    })
  }

  const handleClearFilters = () => {
    setLineSelected(null)
    setValidationSelected(null)
    setSelectedStartDate(null)
    setSelectedEndDate(null)
    setDateTypeRadioButton('recorded')
    setExclude(null)
    setIncludeHidden(false)
    setSelectedLabels([])
    setUrl((currentUrl) => new URL(currentUrl.origin + currentUrl.pathname))
  }

  const searchParams = useMemo(
    () => ({
      ...paramsWithout(urlParams, [
        'institute_ids',
        'is_reviewed',
        'is_processed'
      ]),
      institute_ids: instituteIds,
      is_reviewed: true,
      is_processed: true
    }),
    [instituteIds, urlParams]
  )

  const columns = useMemo(() => [
    { field: 'id', hide: true },
    { field: 'metric', hide: true },
    { field: 'line', hide: true },
    { field: 'vial', hide: true },
    { field: 'validation', hide: true },
    {
      field: 'date',
      headerName: 'Recorded',
      width: 190,
      renderCell: (params) => {
        const { id, date, validation } = params.row

        return (
          <Validated
            justifyContent="flex-start"
            validated={validation}
            childrenSpacing={1}
            validationId={validation && validation.id}
          >
            <StyledLink
              to={{
                pathname: `/record/${id}`,
                state: { name: 'Cross Organization' }
              }}
            >
              {date
                ? format(parseJSON(date), 'dd MMM yyyy HH:mm')
                : 'Unprocessed Record'}
            </StyledLink>
          </Validated>
        )
      }
    },
    {
      field: 'institute',
      headerName: 'Organization',
      width: 170,
      renderCell: ({ value }) => value?.name || '-'
    },
    {
      field: 'vial__uuid',
      headerName: 'VitalVial',
      width: 90,
      renderCell: ({ row }) => row.vial || '-'
    },
    {
      field: 'upload_date',
      headerName: 'Uploaded',
      width: 120,
      renderCell: ({ value }) =>
        value ? formatDistanceToNowStrict(parseJSON(value), { addSuffix: true }) : '-'
    },
    {
      field: 'recordmetrics__duration',
      headerName: 'Duration (h:m:s)',
      width: 150,
      renderCell: ({ row }) => {
        const duration = row.metrics?.duration
        return duration ? formatDuration(duration) : '-'
      }
    },
    {
      field: 'recordmetrics__auc',
      renderHeader: () => (
        <Typography style={{ fontSize: '18px', fontWeight: 600 }}>
          {metricLabel}
        </Typography>
      ),
      width: 110,
      renderCell: ({ row }) => row?.metrics?.[metricType] ?? '-'
    },
    {
      field: 'range',
      headerName: 'Range',
      sortable: false,
      flex: 1,
      renderCell: ({ row }) => (
        <Box width={70}>
          {row.out_of_range?.range
            ? `${row.out_of_range.range.lower} - ${row.out_of_range.range.upper}`
            : '-'}
        </Box>
      )
    },
    {
      field: 'line__name',
      headerName: 'Line',
      width: 180,
      renderCell: ({ row }) =>
        row?.line ? (
          <StyledLink
            to={{
              pathname: `/line/${row.line.id}/`,
              state: {
                name: 'Cross Organization',
                referrer: {
                  path: `${window.location.pathname}${window.location.search}`
                }
              }
            }}
          >
            {row.line.name}
          </StyledLink>
        ) : null
    },
    {
      field: 'labels',
      headerName: 'Labels',
      sortable: false,
      flex: 1,
      renderCell: ({ row }) => (
        <Labels
          shownLabels={row?.labels && row.labels.slice(0, 2)}
          hiddenLabels={row?.labels && row.labels.slice(2)}
          display="flex"
          flexDirection="column"
          flexGrow={1}
          justifyContent="flex-start"
        />
      )
    },
    {
      field: 'flags',
      headerName: 'Flags',
      sortable: false,
      flex: 1,
      renderCell: ({ row }) => <RecordFlags record={row} />
    }
  ], [metricLabel, metricType])

  const selectedLine =
    lineResponse?.items?.find((line) => line.id === lineSelected) || null
  const selectedValidation =
    validationOptions.find((validation) => validation.id === validationSelected) ||
    null

  return (
    <Box display="flex" flexDirection="column" gap={2} pb={3}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography fontWeight={600}>Labels</Typography>
            <Button size="small" color="secondary" onClick={() => handleSelectedLabels(null, [])}>
              Reset
            </Button>
          </Box>
          <LabelToggle
            labels={labelOptions}
            selectedLabels={selectedLabels}
            handleSelectedLabels={handleSelectedLabels}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography fontWeight={600} mb={1}>
            Filter By Line
          </Typography>
          <Autocomplete
            size="small"
            options={lineResponse?.items || []}
            value={selectedLine}
            loading={isLinesLoading}
            onChange={handleLineChange}
            getOptionLabel={(option) =>
              `${option.name}${option.institute ? ` (${option.institute.name})` : ''}`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Select Line" />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography fontWeight={600} mb={1}>
            Filter By Validation
          </Typography>
          <Autocomplete
            size="small"
            options={validationOptions}
            value={selectedValidation}
            loading={isValidationsLoading}
            onChange={handleValidationChange}
            getOptionLabel={(option) =>
              `${option.name}${option.institute ? ` (${option.institute.name})` : ''}`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Select Validation" />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Typography fontWeight={600} mb={1}>
            Flags
          </Typography>
          <ToggleFlag
            value={exclude}
            onChange={handleExclude}
            trueLabel="Excluded"
            falseLabel="Not Excluded"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" flexWrap="wrap">
            <Typography fontWeight={600}>Date</Typography>
            <RadioGroup
              name="cross-org-record-date"
              value={dateTypeRadioButton}
              onChange={handleRadioButtonChange}
              row
              sx={{ ml: 1, alignItems: 'center' }}
            >
              <FormControlLabel
                control={<Radio color="primary" size="small" />}
                value="recorded"
                label="Recorded"
                sx={{ ml: 0, mr: 2 }}
              />
              <FormControlLabel
                control={<Radio color="primary" size="small" />}
                value="uploaded"
                label="Uploaded"
                sx={{ ml: 0, mr: 0 }}
              />
            </RadioGroup>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <DatePicker
              label="Start"
              value={selectedStartDate}
              disableMaskedInput
              inputFormat="dd MMM yyyy"
              clearable
              onChange={handleStartDateChange}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
            <DatePicker
              label="End"
              value={selectedEndDate}
              disableMaskedInput
              inputFormat="dd MMM yyyy"
              clearable
              onChange={handleEndDateChange}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            <DropDownSelect
              labelId="cross-org-num-records-select"
              label="Records per Page"
              value={numRecords}
              onValueChange={setNumRecords}
              items={recordsPerPageOptions}
              styles={{ width: '130px' }}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Role restrict={['staff']}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={includeHidden}
                  onChange={handleIncludeHidden}
                  name="includeHidden"
                />
              }
              label="Include Hidden Records"
            />
          </Role>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={handleClearFilters}
            sx={{
              marginTop: '10px',
              marginBottom: '5px',
              fontSize: '12px'
            }}
          >
            Clear Filters
          </Button>
        </Grid>
      </Grid>
      <ServerSideDataGrid
        apiCall={api.crossOrg.records.list}
        queryName="crossOrgRecords"
        searchParams={searchParams}
        columns={columns}
        noRowsText="No records found for the selected organizations"
        pageSize={Number(numRecords)}
        rowHeight={35}
        reserveViewportHeight={false}
        sx={{
          '& .MuiDataGrid-dataContainer': {
            minHeight: 'auto !important'
          },
          '& .MuiDataGrid-row': {
            maxHeight: '35px !important',
            minHeight: '35px !important'
          }
        }}
      />
    </Box>
  )
}

function getLineOptionLabel(option) {
  return `${option.name}${option.institute ? ` (${option.institute.name})` : ''}`
}

function metricValue(value, suffix = '') {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  if (typeof value === 'number') {
    return `${Number.isInteger(value) ? value : value.toFixed(1)}${suffix}`
  }

  return `${value}${suffix}`
}

function metricRange(range) {
  if (!range || range.lower === undefined || range.upper === undefined) {
    return '-'
  }

  return `${metricValue(range.lower)} - ${metricValue(range.upper)}`
}

function lineStatsSource(line) {
  if (line.validation) {
    return {
      label: 'Validation',
      stats: line.validation
    }
  }

  if (line.line_statistics) {
    return {
      label: 'Line Statistics',
      stats: line.line_statistics
    }
  }

  return {
    label: '-',
    stats: null
  }
}

const lineStatsColumns = [
  ['Line', null, 'left'],
  ['Organization', null, 'left'],
  ['Records'],
  ['Length'],
  ['Baseline', 'The source of the statistics: validation records when a validation is available, or the line statistics otherwise.', 'left'],
  ['Mean', 'The average value of the selected metric across the baseline records.'],
  ['Std Dev', 'Standard deviation: how spread out the values are around the mean. A larger value means more variation.'],
  ['CV', 'Coefficient of variation: standard deviation divided by the mean, expressed as a percentage. It shows variability relative to the average.'],
  ['Range', 'The expected lower and upper limits for the selected metric, calculated from the baseline. These limits appear as dashed lines on the chart.'],
  ['Min', 'The lowest value of the selected metric in the baseline records.'],
  ['Q1', 'First quartile (25th percentile): about 25% of the baseline values are at or below this value.'],
  ['Median', 'The middle value (50th percentile): half of the baseline values are at or below this value.'],
  ['Q3', 'Third quartile (75th percentile): about 75% of the baseline values are at or below this value.'],
  ['Max', 'The highest value of the selected metric in the baseline records.']
]

function CrossOrgLineStatsTable({ lines, metricType }) {
  return (
    <TableContainer>
      <Table size="small" sx={{ minWidth: 1200 }}>
        <TableHead>
          <TableRow>
            {lineStatsColumns.map(([label, description, align = 'right']) => (
              <TableCell key={label} align={align} scope="col">
                {description ? (
                  <Tooltip title={description} arrow describeChild>
                    <Box
                      component="span"
                      tabIndex={0}
                      sx={{
                        cursor: 'help',
                        textDecoration: 'underline dotted',
                        textUnderlineOffset: '4px'
                      }}
                    >
                      {label}
                    </Box>
                  </Tooltip>
                ) : label}
              </TableCell>
            ))}
            <TableCell scope="col">
              <Box display="inline-flex" alignItems="center" gap={0.5} whiteSpace="nowrap">
                Rejection Rates
                <Tooltip
                  title="Rejection probabilities are calculated using each organization's analyzer settings and hemolysis thresholds. Differences in these settings can affect the probabilities, so rates across organizations may not be directly comparable."
                  arrow
                  describeChild
                >
                  <IconButton size="small" aria-label="About rejection rate comparisons">
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => {
            const source = lineStatsSource(line)
            const stats = source.stats
            const metricStats = stats?.[metricType]

            return (
              <TableRow key={line.id}>
                <TableCell>{line.name}</TableCell>
                <TableCell>{line.institute?.name || '-'}</TableCell>
                <TableCell align="right">
                  {metricValue(stats?.record_count)}
                </TableCell>
                <TableCell align="right">
                  {line.length ? `${line.length} m` : '-'}
                </TableCell>
                <TableCell>{source.label}</TableCell>
                <TableCell align="right">
                  {metricValue(metricStats?.mean)}
                </TableCell>
                <TableCell align="right">
                  {metricValue(metricStats?.stddev)}
                </TableCell>
                <TableCell align="right">
                  {metricValue(metricStats?.cv, '%')}
                </TableCell>
                <TableCell align="right">
                  {metricRange(metricStats?.range)}
                </TableCell>
                <TableCell align="right">
                  {metricValue(metricStats?.min)}
                </TableCell>
                <TableCell align="right">
                  {metricValue(metricStats?.q1)}
                </TableCell>
                <TableCell align="right">
                  {metricValue(metricStats?.median)}
                </TableCell>
                <TableCell align="right">
                  {metricValue(metricStats?.q3)}
                </TableCell>
                <TableCell align="right">
                  {metricValue(metricStats?.max)}
                </TableCell>
                <TableCell>
                  {metricStats?.rejection_rates ? (
                    <TableRejectionRates rejectionRates={metricStats.rejection_rates} />
                  ) : '-'}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export function CrossOrgCompareLinesPlot({
  records = [],
  selectedLines = [],
  downloadImageFormat = 'png'
}) {
  const history = useHistory()
  const { metricType, metricLabel } = useMetricsContext()
  const chartMetricLabel =
    typeof metricLabel === 'string' ? metricLabel : 'VitalMetric (Beta)'
  const selectedLineIds = useMemo(
    () => new Set(selectedLines.map((line) => line.id)),
    [selectedLines]
  )

  const recordsByLineId = useMemo(() => {
    return records.reduce((groups, record) => {
      const lineId = record.line?.id
      const value = record.metrics?.[metricType]

      if (
        !lineId ||
        !selectedLineIds.has(lineId) ||
        value === null ||
        value === undefined ||
        !record.date
      ) {
        return groups
      }

      if (!groups[lineId]) {
        groups[lineId] = []
      }
      groups[lineId].push(record)
      return groups
    }, {})
  }, [metricType, records, selectedLineIds])

  const plottedLines = selectedLines
    .map((line, index) => {
      const lineRecords = recordsByLineId[line.id] || []
      if (lineRecords.length === 0) {
        return null
      }

      const lineName = getLineOptionLabel(line)
      const color =
        COMPARE_RECRODS_GRAPH_COLORS[index % COMPARE_RECRODS_GRAPH_COLORS.length]

      return { line, lineRecords, lineName, color }
    })
    .filter(Boolean)

  const traces = plottedLines.map(({ lineRecords, lineName, color }) => {
    return {
      x: lineRecords.map((record) => parseJSON(record.date)),
      y: lineRecords.map((record) => record.metrics?.[metricType]),
      url: lineRecords.map((record) => `/record/${record.id}`),
      text: lineRecords.map((record) => {
        const temperature = record.metrics?.mean_kinetic_temperature
        return [
          `Line : ${lineName}`,
          `Date : ${format(parseJSON(record.date), 'dd MMM yyyy HH:mm')}`,
          `${chartMetricLabel} : ${record.metrics?.[metricType]}`,
          `Mean Kinetic Temperature : ${metricValue(temperature)}`
        ].join('<br>')
      }),
      name: lineName,
      hoverinfo: 'text',
      type: 'scatter',
      connectgaps: true,
      mode: 'lines+markers',
      line: {
        color,
        width: 2
      },
      marker: {
        color: '#fff',
        size: 10,
        opacity: 1,
        line: {
          color,
          width: 2
        }
      }
    }
  })

  const recordTimestamps = plottedLines.flatMap(({ lineRecords }) =>
    lineRecords.map((record) => parseJSON(record.date).getTime())
  )
  const firstTimestamp = recordTimestamps.reduce(
    (earliest, timestamp) => Math.min(earliest, timestamp),
    Infinity
  )
  const lastTimestamp = recordTimestamps.reduce(
    (latest, timestamp) => Math.max(latest, timestamp),
    -Infinity
  )
  const singleDatePadding = firstTimestamp === lastTimestamp ? 15000 : 0
  const boundaryDates = Array.from({ length: 100 }, (_, index) => {
    const start = firstTimestamp - singleDatePadding
    const end = lastTimestamp + singleDatePadding
    return new Date(start + ((end - start) * index) / 99)
  })

  const boundaryTraces = plottedLines.flatMap(({ line, lineName, color }) => {
    const range = lineStatsSource(line).stats?.[metricType]?.range

    if (range?.lower == null || range?.upper == null) {
      return []
    }

    const lower = Number(range.lower)
    const upper = Number(range.upper)

    if (!Number.isFinite(lower) || !Number.isFinite(upper)) {
      return []
    }

    return [
      { label: 'Lower out-of-bounds boundary', value: lower },
      { label: 'Upper out-of-bounds boundary', value: upper }
    ].map(({ label, value }) => ({
      x: boundaryDates,
      y: new Array(boundaryDates.length).fill(value),
      name: `${lineName} - ${label}`,
      type: 'scatter',
      mode: 'lines',
      showlegend: false,
      hovertemplate: `${lineName}<br>${label}: %{y}<extra></extra>`,
      meta: {
        boundary: label,
        lineId: line.id
      },
      line: {
        color,
        dash: 'dash',
        width: 4
      }
    }))
  })

  if (traces.length === 0) {
    return (
      <InlineError
        title="No Data"
        detail="There are no processed records for the selected lines."
      />
    )
  }

  return (
    <Plot
      data={[...traces, ...boundaryTraces]}
      layout={{
        autosize: true,
        hovermode: 'closest',
        showlegend: true,
        legend: {
          orientation: 'h',
          yanchor: 'bottom',
          y: 1.05
        },
        margin: {
          l: 75,
          r: 40,
          b: 100,
          t: 40,
          pad: 4
        },
        xaxis: {
          tickfont: {
            size: 13,
            color: '#000000'
          }
        },
        yaxis: {
          title: {
            text: `<b style='font-weight:600 !important'>${chartMetricLabel}</b>`
          },
          fixedrange: true,
          zeroline: false
        }
      }}
      useResizeHandler
      style={{ width: '100%', height: '500px' }}
      config={{
        responsive: true,
        displaylogo: false,
        toImageButtonOptions: {
          format: downloadImageFormat,
          filename: 'cross-org-compare-lines'
        }
      }}
      onClick={(event) => {
        const point = event.points?.[0]
        const url = point?.data?.url?.[point.pointIndex]
        if (url) {
          history.push({ pathname: url, state: { name: 'Cross Organization' } })
        }
      }}
    />
  )
}

export function CrossOrgCompareLinesWorkspace({
  instituteIds,
  headerContent = null
}) {
  const { metricType } = useMetricsContext()
  const previousInstituteIdsRef = useRef(instituteIds)
  const [selectedLines, setSelectedLines] = useState([])
  const [downloadImageFormat, setDownloadImageFormat] = useState('png')

  const lineIds = useMemo(
    () => selectedLines.map((line) => line.id).join(','),
    [selectedLines]
  )

  const linesQuery = useAllRows({
    queryName: 'cross-org-compare-lines-options',
    apiCall: api.crossOrg.lines.list,
    params: {
      institute_ids: instituteIds,
      archived: false
    },
    enabled: Boolean(instituteIds)
  })

  const recordsQuery = useAllRows({
    queryName: 'cross-org-compare-lines-records',
    apiCall: api.crossOrg.records.list,
    params: {
      institute_ids: instituteIds,
      line_ids: lineIds,
      is_processed: true,
      is_reviewed: true,
      exclude: false,
      ordering: 'date'
    },
    enabled: Boolean(instituteIds) && selectedLines.length >= 2
  })

  useEffect(() => {
    if (previousInstituteIdsRef.current !== instituteIds) {
      setSelectedLines([])
    }
    previousInstituteIdsRef.current = instituteIds
  }, [instituteIds])

  return (
    <MainContent>
      <Box p={4} display="flex" flexDirection="column" gap={3}>
        {headerContent}

        <Box display="flex" flexDirection="column" gap={2}>
          <Autocomplete
            size="small"
            multiple
            options={linesQuery.list?.items || []}
            value={selectedLines}
            loading={linesQuery.isFetching}
            onChange={(event, nextLines) => setSelectedLines(nextLines)}
            getOptionLabel={getLineOptionLabel}
            getOptionKey={(option) => option.id}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField {...params} label="Select Lines" />
            )}
          />
          <ListLoadingStatus query={linesQuery} />

          {selectedLines.length < 2 ? (
            <Box py={6} display="flex" justifyContent="center">
              <Typography color="text.secondary">
                Select at least two lines to compare line statistics and
                monitoring records.
              </Typography>
            </Box>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Line Statistics</Typography>
                <DropDownSelect
                  labelId="compare-lines-image-format-select"
                  label="Image Format"
                  value={downloadImageFormat}
                  onValueChange={setDownloadImageFormat}
                  items={[
                    { content: 'PNG', value: 'png' },
                    { content: 'SVG', value: 'svg' }
                  ]}
                  styles={{ minWidth: '130px', maxHeight: '40px' }}
                />
              </Box>

              <CrossOrgLineStatsTable
                lines={selectedLines}
                metricType={metricType}
              />

              <Typography variant="h6">Monitoring Records</Typography>
              <ListLoadingStatus query={recordsQuery} />
              {!recordsQuery.isLoading &&
              !recordsQuery.hasNextPage &&
              !recordsQuery.isError ? (
                <CrossOrgCompareLinesPlot
                  records={recordsQuery.list?.items || []}
                  selectedLines={selectedLines}
                  downloadImageFormat={downloadImageFormat}
                />
              ) : null}
            </>
          )}
        </Box>
      </Box>
    </MainContent>
  )
}

function CrossOrgHeader({
  institutes,
  isInstitutesLoading,
  selectedInstitutes,
  onInstitutesChange,
  recordSummary,
  view,
  onViewChange
}) {
  return (
    <>
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant="h1">Cross Organization</Typography>
        <Typography color="text.secondary">
          Compare lines and records across the organizations assigned to this
          user.
        </Typography>
      </Box>

      <OrganizationSelector
        institutes={institutes}
        loading={isInstitutesLoading}
        value={selectedInstitutes}
        onChange={onInstitutesChange}
      />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <StyledTabs
          value={['compare', 'compare-lines'].includes(view) ? false : view}
          onChange={onViewChange}
        >
          <StyledTab disableRipple label="Lines" value="lines" />
          <StyledTab
            disableRipple
            label={`Records (${recordSummary?.reviewed ?? 0})`}
            value="records"
          />
        </StyledTabs>
        <Box display="flex" alignItems="center" gap={2}>
          {view !== 'compare' ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<CompareArrowsIcon />}
              onClick={() => onViewChange(null, 'compare')}
            >
              Compare Records
            </Button>
          ) : null}
          {view !== 'compare-lines' ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<ShowChartIcon />}
              onClick={() => onViewChange(null, 'compare-lines')}
            >
              Compare Lines
            </Button>
          ) : null}
          <Typography color="text.secondary">
            {selectedInstitutes.length} organization
            {selectedInstitutes.length === 1 ? '' : 's'} selected
          </Typography>
        </Box>
      </Box>
    </>
  )
}

export default function CrossOrg() {
  const history = useHistory()
  const { view: routeView } = useParams()
  const [currentUser, setCurrentUser] = useState(
    authenticationService.currentUserValue
  )
  const [selectedInstitutes, setSelectedInstitutes] = useState([])
  const [initializedSelection, setInitializedSelection] = useState(false)

  useEffect(() => {
    const subscription = authenticationService.currentUser.subscribe((user) => {
      setCurrentUser(user)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const crossOrganizationEnabled = Boolean(currentUser?.cross_organization)

  const normalizedView = [
    'lines',
    'records',
    'compare',
    'compare-lines'
  ].includes(routeView)
      ? routeView
      : 'lines'

  const { data: instituteResponse, isLoading: isInstitutesLoading } = useQuery(
    ['cross-org-institutes'],
    () =>
      api.institutes.list({
        ordering: 'name',
        page: 0,
        size: 1000,
        exclude_archived: true
      }),
    {
      enabled: crossOrganizationEnabled
    }
  )

  useEffect(() => {
    if (
      !initializedSelection &&
      instituteResponse?.items &&
      instituteResponse.items.length > 0
    ) {
      setSelectedInstitutes(instituteResponse.items)
      setInitializedSelection(true)
    }
  }, [initializedSelection, instituteResponse])

  const selectedInstituteIds = useMemo(
    () => selectedInstitutes.map((institute) => institute.id).join(','),
    [selectedInstitutes]
  )

  const { data: recordSummary } = useQuery(
    ['cross-org-record-summary', selectedInstituteIds],
    () =>
      api.crossOrg.records.summary({
        institute_ids: selectedInstituteIds
      }),
    {
      enabled: Boolean(selectedInstituteIds)
    }
  )

  const handleViewChange = (event, nextValue) => {
    const nextPath =
      nextValue === 'lines' ? '/cross-org' : `/cross-org/${nextValue}`
    history.replace(nextPath)
  }

  if (!crossOrganizationEnabled) {
    return (
      <MainContent>
        <Box p={4}>
          <Typography variant="h1">Cross Organization</Typography>
          <Typography mt={2}>
            Cross Organization access is not enabled for this user.
          </Typography>
        </Box>
      </MainContent>
    )
  }

  const headerContent = (
    <CrossOrgHeader
      institutes={instituteResponse?.items || []}
      isInstitutesLoading={isInstitutesLoading}
      selectedInstitutes={selectedInstitutes}
      onInstitutesChange={setSelectedInstitutes}
      recordSummary={recordSummary}
      view={normalizedView}
      onViewChange={handleViewChange}
    />
  )

  if (normalizedView === 'compare') {
    return (
      <CompareRecordsWorkspace
        scope="cross-org"
        instituteIds={selectedInstituteIds}
        headerContent={headerContent}
      />
    )
  }

  if (normalizedView === 'compare-lines') {
    return (
      <CrossOrgCompareLinesWorkspace
        instituteIds={selectedInstituteIds}
        headerContent={headerContent}
      />
    )
  }

  return (
    <MainContent>
      <Box p={4} display="flex" flexDirection="column" gap={3}>
        {headerContent}

        {selectedInstituteIds ? (
          normalizedView === 'lines' ? (
            <CrossOrgLinesTable instituteIds={selectedInstituteIds} />
          ) : (
            <CrossOrgRecordsTable instituteIds={selectedInstituteIds} />
          )
        ) : (
          <Box py={6} display="flex" justifyContent="center">
            <Typography color="text.secondary">
              Select at least one organization to load Cross Organization data.
            </Typography>
          </Box>
        )}
      </Box>
    </MainContent>
  )
}
